import { z } from 'zod';
import { MAIN_ROADMAP_NAME } from './main-roadmap-name';

export const VIEW_MAX_NAME_LENGTH = 64;

const baseViewFields = {
  viewId: z.number().int(),
  organizationId: z.string().min(1),
  loopId: z.number().int(),
  name: z.string().max(VIEW_MAX_NAME_LENGTH).optional(),
  isMain: z.boolean().default(false),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
};

export const lockedViewSchema = z.object({
  ...baseViewFields,
  locked: z.literal(true),
  lockedAt: z.string().datetime(),
  lockedBy: z.string().min(1),
});

export const unlockedViewSchema = z.object({
  ...baseViewFields,
  locked: z.literal(false),
  lockedAt: z.null(),
  lockedBy: z.null(),
});

export const viewDTOSchema = z.discriminatedUnion('locked', [lockedViewSchema, unlockedViewSchema]);

export type LockedView = z.infer<typeof lockedViewSchema>;
export type UnlockedView = z.infer<typeof unlockedViewSchema>;

/**
 * A view is always either locked or unlocked.
 *
 * Design note: ViewDTO is a union of LockedView | UnlockedView so that
 * TypeScript narrows exhaustively through the isLocked/isUnlocked type guards.
 * The Zod discriminated union schema (viewDTOSchema) enforces the locked/lockedAt/lockedBy
 * consistency invariant at runtime; the union type encodes that same
 * invariant at the type level, giving us both runtime safety and compile-time
 * exhaustive narrowing.
 */
export type ViewDTO = LockedView | UnlockedView;

/**
 * Parse raw data through the Zod schema and return a properly typed ViewDTO.
 */
export function parseViewDTO(data: unknown): ViewDTO {
  return viewDTOSchema.parse(data) as ViewDTO;
}

/**
 * Type guard to determine if a view is locked.
 * When this returns true, TypeScript narrows the type to LockedView,
 * guaranteeing both lockedAt and lockedBy are non-null strings.
 */
export function isLocked(view: ViewDTO): view is LockedView {
  return view.locked;
}

export function isUnlocked(view: ViewDTO): view is UnlockedView {
  return !view.locked;
}

/**
 * Request schema for locking a view.
 * Currently empty but extensible for future lock options (e.g., lock duration, reason).
 */
export const lockViewRequestSchema = z.object({}).strict();

export type LockViewRequest = z.infer<typeof lockViewRequestSchema>;

export function isAutoGeneratedName(name: string = ''): boolean {
  const autoGeneratedPattern = /^(View|Swimlane|Timeline|Table)\s+\(\d+\)$/;
  return autoGeneratedPattern.test(name);
}

// Use generated name, e.g., "View (3)" if the view has no name set
export function getViewDisplayName(view: ViewDTO): string {
  if (view.isMain) return MAIN_ROADMAP_NAME;
  return view.name?.trim() || `View (${view.viewId})`;
}

export function validateView(data: { name?: string | null }, ctx: z.RefinementCtx) {
  if (data.name && isAutoGeneratedName(data.name)) {
    ctx.addIssue({
      code: 'custom',
      message: 'View name cannot use auto-generated format. Please choose a different name.',
      path: ['name'],
    });
  }
}

export const viewInsertDTOSchema = z
  .object({
    loopId: z.number().int(),
    name: z.string().max(VIEW_MAX_NAME_LENGTH).optional().nullable(),
  })
  .strict()
  .superRefine(validateView);

export const viewUpdateDTOSchema = z
  .object({
    name: z.string().max(VIEW_MAX_NAME_LENGTH).optional().nullable(),
  })
  .strict()
  .superRefine(validateView);

export type ViewInsertDTO = z.infer<typeof viewInsertDTOSchema>;
export type ViewUpdateDTO = z.infer<typeof viewUpdateDTOSchema>;
export type ViewUpsertDTO = ViewInsertDTO | ViewUpdateDTO;

export const unlockPreviewItemSchema = z.object({
  id: z.number(),
  name: z.string().optional(),
});

export const unlockPreviewResponseSchema = z.object({
  removedItems: z.array(unlockPreviewItemSchema),
  lockedByDisplayName: z.string(),
  lockedAt: z.string().datetime(),
});

export type UnlockPreviewItem = z.infer<typeof unlockPreviewItemSchema>;
export type UnlockPreviewResponse = z.infer<typeof unlockPreviewResponseSchema>;
